import {
    ApplicationInsights,
    ITelemetryItem,
} from '@microsoft/applicationinsights-web';
import {
    getEnvironment,
    sanitizeUrl,
} from '@uipath/portal-shell-util';
import {
    AppInsightDriverBuilder,
    DeploymentType,
    Tracker,
    WellKnownApplication,
} from '@uipath/telemetry-client-web';
import {
    TrackPlatformAll,
    TrackPlatformInfo,
} from '@uipath/telemetry-client-web/models';

import { IAppInsightsModel } from '../../models/appInsights/appInsightsModel';
import { TelemetryEventMap } from '../../models/appInsights/eventMap';
import { IAppInsightsConfig } from '../../models/globalScript';
import { getEnvConfigField } from '../EnvironmentUtil';
import Logger from '../logger/logger';
import {
    getAccountName,
    getSpaceName,
    getTenantName,
} from '../urlHelper';
import { addGlobalUnHandledExceptionCatcher } from './globalUnhandledExceptions';

export class AppInsightsClient implements IAppInsightsModel {
    private _tracker: Tracker<TelemetryEventMap>;
    private _platformInfo: TrackPlatformAll = {};

    constructor(opts?: IAppInsightsConfig) {
        const instance = this._getAppInsightsInstance(opts);

        if (!instance) {
            return;
        }

        Logger.debug('AppInsights', `Setting up driver and tracker`);
        this._tracker = new Tracker<TelemetryEventMap>(
            this._deploymentType,
            WellKnownApplication.PortalShell,
            AppInsightDriverBuilder.withInstance(instance).build(),
        );
    }

    public get trackerMethods() {
        return this._tracker ? {
            trackEvent: this._tracker.trackEvent.bind(this._tracker),
            trackNavigation: this._tracker.trackNavigation.bind(this._tracker),
            trackLogin: this._tracker.trackLogin.bind(this._tracker),
            trackApplicationStart: this._tracker.trackApplicationStart.bind(this._tracker),
            trackError: this._tracker.trackError.bind(this._tracker),
            trackTrace: this._tracker.trackTrace.bind(this._tracker),
        } : undefined;
    }

    public setPlatformInfo(platformInfo: TrackPlatformAll) {
        this._platformInfo = {
            ...this._platformInfo,
            ...platformInfo,
        };
        Logger.debug('AppInsights', `Setting platform info with new data: ${JSON.stringify(platformInfo)}`);

        this._tracker?.setPlatformInfo(this._getPlatformInfoByDeployment());
    }

    public getPlatformInfoField(field: keyof TrackPlatformAll) {
        return this._platformInfo[field];
    }

    public getPlatformInfo() {
        return this._platformInfo;
    }

    public initializeTracker(devMode: boolean, language: string) {
        Logger.debug('AppInsights', `Initializing tracker with devMode:${devMode}, language:${language}`);
        this._tracker?.initialize({
            isDevelopment: devMode,
            isPageViewTrackingEnabled: true,
            isPageViewPerformanceTrackingEnabled: true,
            pageViewTrackingOptions: {
                name: document.title,
                uri: sanitizeUrl(location.href),
            },
            language,
            initializer: this._telemetryInitializer,
        });

        this._tracker?.trackNavigation({ targetRoute: sanitizeUrl(location.href) });

        addGlobalUnHandledExceptionCatcher(this);
    }

    public setLanguage(language: string) {
        Logger.debug('AppInsights', `Changing tracker language to ${language}`);
        this._tracker?.setLanguage(language);
    }

    private _telemetryInitializer(envelope: ITelemetryItem) {
        if (envelope.tags) {
            envelope.tags['ai.cloud.role'] = 'apollo';
            envelope.tags['apollo.spaceName'] = getSpaceName();
            envelope.tags['apollo.accountName'] = getAccountName();
            envelope.tags['apollo.tenantName'] = getTenantName();
        }

        // PLT-13335: Sanitize query / token parameters
        if (envelope.data?.baseData?.url) {
            const originalUrl = envelope.data.baseData.url;
            envelope.data.baseData.url = sanitizeUrl(originalUrl);
        }

        return undefined;
    }

    private _getPlatformInfoByDeployment(): TrackPlatformInfo {
        const {
            productVersion = '',
            cloudTenantId = '',
            cloudOrganizationId = '',
            sessionId = '',
            cloudUserId = '',
            installationId = '',
            clusterId = '',
            localUserId = '',
            tenantId = '',
            isHost,
        } = this._platformInfo;

        switch (this._deploymentType) {
            case DeploymentType.Standalone:
                return {
                    localUserId: localUserId,
                    tenantId: tenantId,
                    sessionId: sessionId,
                    isHost,
                    installationId: installationId,
                    productVersion: productVersion,
                };
            case DeploymentType.AutomationCloud:
                return {
                    cloudUserId: cloudUserId,
                    cloudOrganizationId: cloudOrganizationId,
                    cloudTenantId: cloudTenantId,
                    sessionId: sessionId,
                    productVersion: productVersion,
                };
            case DeploymentType.AutomationSuite:
                return {
                    cloudUserId: cloudUserId,
                    cloudOrganizationId: cloudOrganizationId,
                    cloudTenantId: cloudTenantId,
                    sessionId: sessionId,
                    isHost,
                    productVersion: productVersion,
                    clusterId: clusterId,
                };
        }
    }

    private get _deploymentType() {
        const env = getEnvironment();

        return env === 'k8s'
            ? DeploymentType.AutomationSuite
            : env === 'msi'
                ? DeploymentType.Standalone
                : DeploymentType.AutomationCloud;
    }

    private _getAppInsightsInstance(opts?: IAppInsightsConfig) {
        let appInsightsKey = getEnvConfigField('APPINSIGHTS_KEY')
            || (window as any).env?.APPLICATION_INSIGHTS_INSTRUMENTATION_KEY;
        let appInsightsString = getEnvConfigField('APPINSIGHTS_CONNECTION_STRING')
            || (window as any).env?.APPLICATION_INSIGHTS_CONNECTION_STRING;

        if (opts?.instrumentationKey && opts?.connectionString) {
            appInsightsKey = opts.instrumentationKey;
            appInsightsString = opts.connectionString;
        }

        const noKeyOrString = !appInsightsKey && !appInsightsString;

        if (noKeyOrString) {
            Logger.error('AppInsights', 'Error initializing AppInsights: key or connection string is missing');
            return;
        }

        try {
            const appInsights = new ApplicationInsights(
                {
                    config: {
                        instrumentationKey: appInsightsKey,
                        connectionString: appInsightsString,
                        accountId: getAccountName(),
                        appId: getSpaceName(),
                    },
                });

            return appInsights;
        } catch (exception) {
            Logger.error('AppInsights', 'Error initializing AppInsights, exception: ' + JSON.stringify(exception));
            return;
        }
    }
}
